import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AnimatedMessageDemo from '../componets/AnimatedMessageDemo';
import AIConnectorDemo from '../componets/AIAnimatedConnector';

const Landing = () => {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const howItWorksRef = useRef(null);

  const scrollToHowItWorks = () => {
    howItWorksRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    const handleResize = () => setIsMobile(window.innerWidth <= 768);

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const features = [
    {
      icon: '🎤',
      title: 'Voice-First Capture',
      description: 'Simply talk about your conversation. "I met Sam at Courseside Kids, we discussed youth programming initiatives." Loop captures every detail instantly.',
      delay: '0s'
    },
    {
      icon: '🧠',
      title: 'AI Memory Engine',
      description: 'Every interaction, detail, and preference is remembered. Your network\'s context lives in one intelligent system.',
      delay: '0.15s'
    },
    {
      icon: '✍️',
      title: 'Personalized Outreach',
      description: 'Generate perfectly tailored messages based on conversation history, shared interests, and relationship context. No more generic templates.',
      delay: '0.3s'
    },
    {
      icon: '🔍',
      title: 'Smart Research Assistant',
      description: 'Schedule automated research on your contacts. Loop finds relevant news, company updates, and conversation starters.',
      delay: '0.45s'
    },
    {
      icon: '📅',
      title: 'Intelligent Follow-ups',
      description: 'Set reminders and let Loop suggest the perfect timing and messaging for your next touchpoint.',
      delay: '0.6s'
    },
    {
      icon: '🔗',
      title: 'Relationship Timeline',
      description: 'See the complete story of every relationship at a glance. Track touchpoints, shared experiences, and connection strength.',
      delay: '0.75s'
    }
  ];

  const useCases = [
    {
      title: 'Sales Professionals',
      description: 'Never forget a client detail. Follow up with precision. Build relationships that close deals.',
      gradient: 'from-emerald-500 to-teal-600'
    },
    {
      title: 'Entrepreneurs',
      description: 'Manage your growing network effortlessly. Turn casual conversations into lasting partnerships.',
      gradient: 'from-blue-500 to-indigo-600'
    },
    {
      title: 'Job Seekers',
      description: 'Keep every connection warm. Stand out with personalized outreach that shows you remember.',
      gradient: 'from-purple-500 to-pink-600'
    },
    {
      title: 'Consultants',
      description: 'Maintain dozens of client relationships with the personal touch that builds trust and referrals.',
      gradient: 'from-orange-500 to-red-600'
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0a0a0a',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
      overflowX: 'hidden',
    }}>
      {/* Navigation */}
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: scrollY > 50 ? 'rgba(10, 10, 10, 0.95)' : 'transparent',
        backdropFilter: scrollY > 50 ? 'blur(20px)' : 'none',
        borderBottom: scrollY > 50 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
        transition: 'all 0.3s ease',
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <img
              src={`${process.env.PUBLIC_URL}/logo.png`}
              alt="Loop Logo"
              style={{
                width: isMobile ? '160px' : '200px',
                height: isMobile ? '130px' : '160px',
                objectFit: 'contain',
                filter: 'brightness(0) invert(1) drop-shadow(0 10px 30px rgba(0,0,0,0.3))',
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '15px' }}>
            <button
              onClick={() => navigate('/login')}
              style={{
                padding: isMobile ? '10px 20px' : '12px 28px',
                background: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: 'white',
                borderRadius: '12px',
                fontSize: isMobile ? '14px' : '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
              }}
            >
              Sign In
            </button>
            {!isMobile && (
              <button
                onClick={() => navigate('/register')}
                style={{
                  padding: '12px 28px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                  border: 'none',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 20px rgba(59, 130, 246, 0.3)',
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 30px rgba(59, 130, 246, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 20px rgba(59, 130, 246, 0.3)';
                }}
              >
                Get Started
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '100px 20px 60px' : '120px 60px 80px',
        overflow: 'hidden',
      }}>
        {/* Animated Background */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.4,
          background: 'radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.15) 0%, transparent 50%)',
        }} />

        <div style={{
          position: 'relative',
          maxWidth: '1400px',
          width: '100%',
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? '40px' : '60px',
          alignItems: 'center',
        }}>
          {/* Left Column - Text Content */}
          <div style={{
            textAlign: isMobile ? 'center' : 'left',
            animation: 'fadeInUp 1s ease-out',
          }}>
            <div style={{
              display: 'inline-block',
              padding: '8px 20px',
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '50px',
              marginBottom: '30px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#60a5fa',
              animation: 'fadeIn 1s ease-out 0.2s backwards',
            }}>
              🚀 The Future of Professional Networking
            </div>

            <h1 style={{
              fontSize: isMobile ? '42px' : '56px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: 1.1,
              letterSpacing: '-0.03em',
              background: 'linear-gradient(135deg, #ffffff 0%, #a0a0a0 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: 'fadeIn 1s ease-out 0.4s backwards',
            }}>
              Never Forget<br />A Connection
            </h1>

            <p style={{
              fontSize: isMobile ? '18px' : '20px',
              color: '#a3a3a3',
              maxWidth: '600px',
              margin: isMobile ? '0 auto 40px' : '0 0 40px',
              lineHeight: 1.6,
              animation: 'fadeIn 1s ease-out 0.6s backwards',
            }}>
              Your AI-powered networking assistant that remembers every conversation,
              personalizes every message, and keeps your professional relationships thriving.
            </p>

            <div style={{
              display: 'flex',
              gap: '20px',
              justifyContent: isMobile ? 'center' : 'flex-start',
              flexWrap: 'wrap',
              animation: 'fadeIn 1s ease-out 0.8s backwards',
            }}>
              <button
                onClick={() => navigate('/register')}
                style={{
                  padding: isMobile ? '16px 40px' : '18px 50px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                  border: 'none',
                  color: 'white',
                  borderRadius: '14px',
                  fontSize: isMobile ? '16px' : '18px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 10px 40px rgba(59, 130, 246, 0.4)',
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-3px)';
                  e.target.style.boxShadow = '0 15px 50px rgba(59, 130, 246, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 10px 40px rgba(59, 130, 246, 0.4)';
                }}
              >
                Start Free Today
              </button>

              <button
                onClick={scrollToHowItWorks}
                style={{
                  padding: isMobile ? '16px 40px' : '18px 50px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  borderRadius: '14px',
                  fontSize: isMobile ? '16px' : '18px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                }}
              >
                See How It Works
              </button>
            </div>

            {/* Stats - Now in hero section */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '30px',
              marginTop: '60px',
              padding: isMobile ? '30px 20px' : '40px 0',
            }}>
              <div>
                <div style={{
                  fontSize: isMobile ? '32px' : '40px',
                  fontWeight: '800',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  marginBottom: '8px',
                }}>
                  10x
                </div>
                <div style={{
                  fontSize: isMobile ? '12px' : '14px',
                  color: '#a3a3a3',
                }}>
                  More Connections
                </div>
              </div>
              <div>
                <div style={{
                  fontSize: isMobile ? '32px' : '40px',
                  fontWeight: '800',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  marginBottom: '8px',
                }}>
                  80%
                </div>
                <div style={{
                  fontSize: isMobile ? '12px' : '14px',
                  color: '#a3a3a3',
                }}>
                  Time Saved
                </div>
              </div>
              <div>
                <div style={{
                  fontSize: isMobile ? '32px' : '40px',
                  fontWeight: '800',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  marginBottom: '8px',
                }}>
                  100%
                </div>
                <div style={{
                  fontSize: isMobile ? '12px' : '14px',
                  color: '#a3a3a3',
                }}>
                  Personalized
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - AIConnectorDemo */}
          {!isMobile && (
            <div style={{
              animation: 'fadeIn 1s ease-out 1s backwards',
            }}>
              <AIConnectorDemo />
            </div>
          )}
        </div>
      </section>

      {/* Mobile AIConnectorDemo */}
      {isMobile && (
        <section style={{
          padding: '60px 20px',
          background: 'linear-gradient(180deg, #0a0a0a 0%, #0f0f0f 100%)',
        }}>
          <AIConnectorDemo />
        </section>
      )}

      <section style={{
        padding: isMobile ? '80px 20px' : '120px 60px',
        background: 'linear-gradient(180deg, #0f0f0f 0%, #0a0a0a 100%)',
      }}>
        <div style={{
          maxWidth: '1000px',
          margin: '0 auto',
          textAlign: 'center',
        }}>
          <h2 style={{
            fontSize: isMobile ? '36px' : '56px',
            fontWeight: '800',
            marginBottom: '20px',
          }}>
            Ask Anything About Your Network
          </h2>
          <p style={{
            fontSize: isMobile ? '18px' : '20px',
            color: '#a3a3a3',
            marginBottom: '50px',
          }}>
            Loop's AI understands natural language. Just ask.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: '20px',
          }}>
            {[
              '"Who did I meet at the conference last month?"',
              '"Find contacts in Boston working in tech"',
              '"Who should I follow up with this week?"',
              '"Show me everyone interested in sustainability"'
            ].map((query, i) => (
              <div
                key={i}
                style={{
                  padding: '24px',
                  background: 'rgba(59, 130, 246, 0.05)',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  borderRadius: '12px',
                  fontSize: '16px',
                  color: '#a3a3a3',
                  fontStyle: 'italic',
                }}
              >
                {query}
              </div>
            ))}
          </div>
        </div>
      </section>
      <section style={{
        padding: isMobile ? '80px 20px' : '120px 60px',
        background: 'linear-gradient(180deg, #0f0f0f 0%, #0a0a0a 100%)',
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: isMobile ? '60px' : '80px',
          }}>
            <h2 style={{
              fontSize: isMobile ? '36px' : '56px',
              fontWeight: '800',
              marginBottom: '20px',
              letterSpacing: '-0.02em',
            }}>
              See AI Message Generation in Action
            </h2>
            <p style={{
              fontSize: isMobile ? '18px' : '20px',
              color: '#a3a3a3',
              maxWidth: '700px',
              margin: '0 auto',
            }}>
              Watch how Loop creates personalized, context-aware messages and refines them through natural conversation
            </p>
          </div>

          <AnimatedMessageDemo autoPlay={true} loop={true} />
        </div>
      </section>

      {/* How It Works Section */}
      <section
        ref={howItWorksRef}
        style={{
          padding: isMobile ? '80px 20px' : '120px 60px',
          background: 'linear-gradient(180deg, #0a0a0a 0%, #0f0f0f 100%)',
        }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: isMobile ? '60px' : '80px',
          }}>
            <h2 style={{
              fontSize: isMobile ? '36px' : '56px',
              fontWeight: '800',
              marginBottom: '20px',
              letterSpacing: '-0.02em',
            }}>
              How Loop Works
            </h2>
            <p style={{
              fontSize: isMobile ? '18px' : '20px',
              color: '#a3a3a3',
              maxWidth: '700px',
              margin: '0 auto',
            }}>
              From conversation to connection in three simple steps
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: isMobile ? '40px' : '60px',
          }}>
            {[
              {
                number: '01',
                title: 'Capture',
                description: 'Voice chat or manually input your conversation details. "Just met Sarah from TechCorp, discussed AI integration for their platform."',
                icon: '🎯',
                gradient: 'from-blue-500 to-cyan-500'
              },
              {
                number: '02',
                title: 'Enrich',
                description: 'Update with new interactions, notes, or let Loop research relevant information automatically. Every detail builds context.',
                icon: '💡',
                gradient: 'from-purple-500 to-pink-500'
              },
              {
                number: '03',
                title: 'Engage',
                description: 'Generate perfectly tailored messages, schedule follow-ups, and maintain warm relationships effortlessly.',
                icon: '🚀',
                gradient: 'from-emerald-500 to-teal-500'
              }
            ].map((step, index) => (
              <div
                key={index}
                style={{
                  padding: isMobile ? '35px 25px' : '45px 35px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderRadius: '24px',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  animation: `fadeInUp 0.8s ease-out ${index * 0.2}s backwards`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                  e.currentTarget.style.background = 'rgba(59, 130, 246, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                }}
              >
                <div style={{
                  fontSize: '48px',
                  marginBottom: '20px',
                }}>
                  {step.icon}
                </div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '700',
                  color: '#3b82f6',
                  marginBottom: '12px',
                  letterSpacing: '0.1em',
                }}>
                  {step.number}
                </div>
                <h3 style={{
                  fontSize: isMobile ? '24px' : '28px',
                  fontWeight: '700',
                  marginBottom: '16px',
                  letterSpacing: '-0.01em',
                }}>
                  {step.title}
                </h3>
                <p style={{
                  fontSize: '16px',
                  color: '#a3a3a3',
                  lineHeight: 1.7,
                }}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Message Generation Demo */}
      

      {/* Features Grid */}
      <section style={{
        padding: isMobile ? '80px 20px' : '120px 60px',
        background: '#0a0a0a',
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: isMobile ? '60px' : '80px',
          }}>
            <h2 style={{
              fontSize: isMobile ? '36px' : '56px',
              fontWeight: '800',
              marginBottom: '20px',
              letterSpacing: '-0.02em',
            }}>
              Powerful Features
            </h2>
            <p style={{
              fontSize: isMobile ? '18px' : '20px',
              color: '#a3a3a3',
              maxWidth: '700px',
              margin: '0 auto',
            }}>
              Everything you need to build and maintain a thriving professional network
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: isMobile ? '30px' : '40px',
          }}>
            {features.map((feature, index) => (
              <div
                key={index}
                style={{
                  padding: isMobile ? '30px 25px' : '40px 35px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderRadius: '20px',
                  transition: 'all 0.3s ease',
                  animation: `fadeInUp 0.6s ease-out ${feature.delay} backwards`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                }}
              >
                <div style={{
                  fontSize: '36px',
                  marginBottom: '16px',
                }}>
                  {feature.icon}
                </div>
                <h3 style={{
                  fontSize: isMobile ? '20px' : '24px',
                  fontWeight: '700',
                  marginBottom: '12px',
                  letterSpacing: '-0.01em',
                }}>
                  {feature.title}
                </h3>
                <p style={{
                  fontSize: '16px',
                  color: '#a3a3a3',
                  lineHeight: 1.7,
                }}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section style={{
        padding: isMobile ? '80px 20px' : '120px 60px',
        background: 'linear-gradient(180deg, #0a0a0a 0%, #0f0f0f 100%)',
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: isMobile ? '60px' : '80px',
          }}>
            <h2 style={{
              fontSize: isMobile ? '36px' : '56px',
              fontWeight: '800',
              marginBottom: '20px',
              letterSpacing: '-0.02em',
            }}>
              Perfect For
            </h2>
            <p style={{
              fontSize: isMobile ? '18px' : '20px',
              color: '#a3a3a3',
              maxWidth: '700px',
              margin: '0 auto',
            }}>
              Professionals who understand that relationships drive success
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: isMobile ? '25px' : '30px',
          }}>
            {useCases.map((useCase, index) => (
              <div
                key={index}
                style={{
                  padding: isMobile ? '35px 28px' : '45px 40px',
                  background: `linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)`,
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '20px',
                  transition: 'all 0.3s ease',
                  animation: `fadeInUp 0.6s ease-out ${index * 0.15}s backwards`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.4)';
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)';
                }}
              >
                <h3 style={{
                  fontSize: isMobile ? '22px' : '26px',
                  fontWeight: '700',
                  marginBottom: '12px',
                  letterSpacing: '-0.01em',
                }}>
                  {useCase.title}
                </h3>
                <p style={{
                  fontSize: '16px',
                  color: '#a3a3a3',
                  lineHeight: 1.7,
                }}>
                  {useCase.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: isMobile ? '80px 20px' : '120px 60px',
        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.15) 0%, transparent 70%)',
        }} />

        <div style={{
          maxWidth: '900px',
          margin: '0 auto',
          textAlign: 'center',
          position: 'relative',
        }}>
          <h2 style={{
            fontSize: isMobile ? '36px' : '56px',
            fontWeight: '800',
            marginBottom: '24px',
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
          }}>
            Ready to Transform<br />Your Network?
          </h2>
          <p style={{
            fontSize: isMobile ? '18px' : '22px',
            color: '#a3a3a3',
            marginBottom: '50px',
            lineHeight: 1.6,
          }}>
            Join professionals who are building stronger relationships with less effort.
            Start your free account today.
          </p>
          <button
            onClick={() => navigate('/register')}
            style={{
              padding: isMobile ? '18px 50px' : '22px 60px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              border: 'none',
              color: 'white',
              borderRadius: '16px',
              fontSize: isMobile ? '18px' : '20px',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 15px 50px rgba(59, 130, 246, 0.4)',
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-3px)';
              e.target.style.boxShadow = '0 20px 60px rgba(59, 130, 246, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 15px 50px rgba(59, 130, 246, 0.4)';
            }}
          >
            Get Started Free
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        padding: isMobile ? '40px 20px' : '60px',
        background: '#0a0a0a',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'center' : 'center',
          gap: isMobile ? '20px' : '0',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <img
              src={`${process.env.PUBLIC_URL}/logo.png`}
              alt="Loop"
              style={{
                height: '92px',
                width: '92px',
                objectFit: 'contain',
                filter: 'brightness(0) invert(1) drop-shadow(0 10px 30px rgba(0,0,0,0.3))',
              }}
            />
          </div>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            alignItems: isMobile ? 'center' : 'flex-start',
          }}>
            <div style={{
              fontSize: '14px',
              color: '#737373',
              textAlign: isMobile ? 'center' : 'left',
            }}>
              © 2026 Loop. Stay Connected, Stay in the Loop.
            </div>
            <div style={{
              fontSize: '14px',
              color: '#737373',
              textAlign: isMobile ? 'center' : 'left',
            }}>
              https://www.loop.college/
            </div>
            <div style={{
              fontSize: '14px',
              color: '#737373',
              textAlign: isMobile ? 'center' : 'left',
            }}>
              https://loop-site.com/
            </div>
          </div>
        </div>
      </footer>

      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </div>
  );
};

export default Landing;